// targeting-combat.ts — TARGETING / COMBAT WIDGET, carved out of includes/js/template.js
// (originally ~lines 6286-6720) into the typed targeting-combat bundle (XZ closure Phase B,
// 2026-06-13; see plans/xz-frontend-ts-closure.md). Byte-faithful port of the standalone
// includes/js/targeting-combat-init.js — NO refactor (the data-* retirement the plan flagged
// for Phase B already landed 2026-06-12; remaining dataset reads are IDENTITY attrs the token contract keeps).
//
// Two halves:
//   1. Targeting / combat-widget UX (targetObject, updateTargetedObjects, target-all, clear*, AOE anchor).
//   2. Target-state predicates (checkTargetPrivate / statusEffectInstanceIsCampaignPaused /
//      checkTargetHidden / checkTargetInvisible / checkTargetEthereal / checkTargetSpellEffect) —
//      consumed by template.js + the encounter-map (XZVisibility), creature-render and inventory
//      bundles as bare-identifier globals.
//
// Load order: bundle loads AFTER template.js + side-panels-init.js (same slot the .js held). Every
// dependency (FT3D AOE manager / clearSideInfoPanelContent / loadPostFunctions / item-arr-readers
// / template.js globals) is invoked when a handler runs, so call-time global resolution holds
// regardless of which bundles load first. All public names re-attached to
// window at the bottom + the two window.FateTide.* exports inside the body, preserving the historical
// global surface consumed by the FT3D/XZ runtime bundles.

const w = window;

function targetingRecord(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

function activeEncounterArr(): unknown {
    const encounterId = String(window.ftGetActiveEncounterId?.() || '').trim();
    const cache = window.FateTide?.encounterCache;
    return cache instanceof Map ? cache.get(encounterId) : window.FateTide?.encounterArr;
}

function targetingCreatures(encounterValue: unknown = activeEncounterArr()): unknown[] {
    const encounter = targetingRecord(encounterValue);
    return Array.isArray(encounter?.encounter_creatures_arr) ? encounter.encounter_creatures_arr : [];
}

function targetStatuses(target: unknown): unknown[] {
    const statuses: unknown = ftCreatureStatuses(target, 'target');
    return Array.isArray(statuses) ? statuses : [];
}

// =============================================================================

// `stateAxis` names the creature flag this action converges (see the GM visibility toggles in
// turn-widget-gm-controls.ts). It drives two things the bare count could not express: the per-pick
// state badge in the roster, and the DIRECTION word on the Confirm button. Without it a GM picking
// a mix of hidden and visible creatures had no way to know the converging toggle was about to HIDE
// rather than reveal — the single most confusing thing about the old "(2), then confirm" bar.
type GmTargetActionStateAxis = 'hidden' | 'private';

interface GmTargetActionOptions {
    key: string;
    label: string;
    minTargets?: number;
    maxTargets?: number;
    stateAxis?: GmTargetActionStateAxis | null;
    onConfirm: (targetIds: string[]) => void | Promise<void>;
}

interface ActiveGmTargetAction {
    options: Required<Pick<GmTargetActionOptions, 'key' | 'label' | 'minTargets' | 'maxTargets'>>
        & Pick<GmTargetActionOptions, 'onConfirm'>
        & { stateAxis: GmTargetActionStateAxis | null };
    selected: string[];
    busy: boolean;
}

const GM_TARGET_ACTION_PANEL_ID = 'ft_gm_target_action_panel';
let activeGmTargetAction: ActiveGmTargetAction | null = null;

function gmTargetActionFrame(): HTMLIFrameElement | null {
    return document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
}

function postGmTargetActionPickMode(active: boolean): void {
    gmTargetActionFrame()?.contentWindow?.postMessage(
        { type: 'ft3d-campaign-creature-pick-mode-v1', payload: { active } },
        window.location.origin,
    );
}

function postGmTargetActionSelection(entityIds: string[]): void {
    gmTargetActionFrame()?.contentWindow?.postMessage(
        { type: 'ft3d-campaign-target-state', payload: { entityIds } },
        window.location.origin,
    );
}

// Roster fold state — sticky for the session so a GM who collapses it keeps it collapsed across
// arms, and a GM who opens it keeps seeing their picks.
let gmTargetActionRosterOpen = true;

/** Resolve a picked entity id back to its creature node in the live encounter. */
function gmTargetActionCreature(entityId: string): unknown {
    const id = String(entityId ?? '').trim().toUpperCase();
    if (!id) return null;
    return targetingCreatures().find((candidate) => {
        const record = targetingRecord(candidate);
        if (!record) return false;
        const ecid = ftEncounterCreatureField(record.encounter_creatures, 'encounter_creature_id');
        return String(ecid ?? '').trim().toUpperCase() === id;
    }) ?? null;
}

/** Is this creature currently carrying the axis we are toggling? */
function gmTargetActionStateOn(creature: unknown, axis: GmTargetActionStateAxis | null): boolean {
    if (!creature || !axis) return false;
    return axis === 'hidden' ? checkTargetHidden(creature) : checkTargetPrivate(creature);
}

interface GmTargetActionPick {
    id: string;
    creature: unknown;
    resolved: boolean;
    on: boolean;
}

/** Resolve every pick ONCE per render — the roster and the direction label must not disagree. */
function gmTargetActionPicks(): GmTargetActionPick[] {
    if (!activeGmTargetAction) return [];
    const axis = activeGmTargetAction.options.stateAxis;
    return activeGmTargetAction.selected.map((id) => {
        const creature = gmTargetActionCreature(id);
        const resolved = creature !== null && creature !== undefined;
        return { id, creature, resolved, on: resolved ? gmTargetActionStateOn(creature, axis) : false };
    });
}

/**
 * The word for what Confirm will actually DO. The endpoints converge: any pick that is not yet ON
 * turns the whole selection ON; only an all-ON selection turns OFF. Mirror that here EXACTLY so the
 * button never promises the opposite of what the server will do.
 *
 * An UNRESOLVED pick (not in the live encounter cache) makes the direction genuinely unknowable
 * here: the server resolves it anyway through search_encounter_arr_for_item_arr()'s query_d_item_arr
 * fallback and folds its REAL state into ITS converge. Treating it as OFF would force the label to
 * "Hide"/"Make Private" while the server might do the exact opposite — worst on the privacy axis.
 * So we refuse to guess: return null, the button falls back to a neutral "Confirm", and the roster
 * flags the unresolved picks so the GM can drop them.
 */
function gmTargetActionDirection(picks: GmTargetActionPick[]): { verb: string; turningOn: boolean } | null {
    const axis = activeGmTargetAction?.options.stateAxis ?? null;
    if (!axis || picks.length === 0) return null;
    if (picks.some((pick) => !pick.resolved)) return null; // cannot honestly name a direction

    const turningOn = picks.some((pick) => !pick.on);
    const verb = axis === 'hidden'
        ? (turningOn ? 'Hide' : 'Reveal')
        : (turningOn ? 'Make Private' : 'Make Public');
    return { verb, turningOn };
}

/**
 * Picture URLs come from the database. HTML-escaping keeps them inside the quoted attribute, but it
 * does not police the SCHEME — so validate that first and drop anything that is not plainly an image
 * location before it reaches src.
 */
function gmTargetActionSafeImageUrl(value: unknown): string {
    const url = String(value ?? '').trim();
    if (!url) return '';
    return /^(https?:\/\/|\/(?!\/)|data:image\/)/i.test(url) ? url : '';
}

/** One chip per pick: thumbnail, name, current state. Clicking a chip removes it from the selection. */
function gmTargetActionRosterHtml(picks: GmTargetActionPick[]): string {
    if (!activeGmTargetAction) return '';
    const axis = activeGmTargetAction.options.stateAxis;
    const busy = activeGmTargetAction.busy;
    if (picks.length === 0) {
        return '<div class="ft-gta-empty">No creatures selected yet &mdash; click them in the Scene or Combat Widget.</div>';
    }

    const chips = picks.map((pick) => {
        const name    = pick.resolved ? (getCreatureName(pick.creature) || 'Unknown') : 'Not in this encounter';
        const picture = pick.resolved ? selectCreaturePicture(pick.creature) : null;
        const url     = gmTargetActionSafeImageUrl(picture ? pictureField0(picture, 'pic_url') : '');

        const art = url
            ? `<img class="ft-gta-chip-art" src="${escapeHtml(url)}" alt="" style="object-fit:contain">`
            : `<span class="ft-gta-chip-initial">${escapeHtml((name[0] || '?').toUpperCase())}</span>`;

        // Only badge the axis we are actually toggling; a bare pick (Award Luck) shows no state.
        // An unresolved pick gets "unknown" rather than a Visible/Public claim we cannot stand behind.
        let badge = '';
        if (!pick.resolved) {
            badge = '<span class="ft-gta-badge" data-state="unknown">unknown</span>';
        } else if (axis) {
            badge = `<span class="ft-gta-badge" data-state="${pick.on ? 'on' : 'off'}">${
                axis === 'hidden' ? (pick.on ? 'Hidden' : 'Visible') : (pick.on ? 'Private' : 'Public')
            }</span>`;
        }

        return `<button type="button" class="ft-gta-chip"
            title="${escapeHtml(name)} — click to remove from the selection"
            data-ft-gm-target-action="drop"
            data-ft-gm-target-entity="${escapeHtml(pick.id)}"
            ${busy ? 'disabled' : ''}>
            ${art}
            <span class="ft-gta-chip-name">${escapeHtml(name)}</span>
            ${badge}
        </button>`;
    }).join('');

    const stale = picks.filter((pick) => !pick.resolved).length;
    const warn = stale > 0
        ? `<div class="ft-gta-warn">${stale} selected creature${stale === 1 ? ' is' : 's are'} not in this encounter &mdash; remove ${stale === 1 ? 'it' : 'them'} to see which way the toggle will go.</div>`
        : '';

    return `${warn}<div class="ft-gta-roster" data-ft-gm-roster>${chips}</div>`;
}

/**
 * The one thing converging cannot express: "reveal just the hidden ones in a mixed pick". Under
 * convergence that needs two presses (hide everything, then reveal everything) — which is exactly
 * the round trip that leaks an ambush. So when the selection is mixed, offer a one-click trim down
 * to the minority side; the direction label then flips on its own and the GM commits once.
 *
 * Deliberately a TRIM of the local selection, not a second endpoint: the server keeps exactly one
 * converging rule, and nothing new can disagree with it.
 */
function gmTargetActionTrimHtml(picks: GmTargetActionPick[]): string {
    const axis = activeGmTargetAction?.options.stateAxis ?? null;
    if (!axis || activeGmTargetAction?.busy) return '';

    const resolved = picks.filter((pick) => pick.resolved);
    if (resolved.length !== picks.length || resolved.length < 2) return ''; // stale picks: no honest offer
    const on  = resolved.filter((pick) => pick.on).length;
    const off = resolved.length - on;
    if (on === 0 || off === 0) return ''; // not mixed — the plain toggle already says it all

    const verb = axis === 'hidden' ? 'Reveal' : 'Make public';
    const noun = axis === 'hidden' ? 'already hidden' : 'already private';
    return `<div class="ft-gta-warn" style="color:var(--ft-gta-text-muted);">
        Mixed selection &mdash; confirming applies to all ${resolved.length}.
        <button type="button" class="ft-gta-btn" style="padding:.1rem .45rem;font-size:.72rem;margin-left:.35rem;"
            data-ft-gm-target-action="keep-on">${verb} the ${on} ${noun}</button>
    </div>`;
}

function renderGmTargetAction(): void {
    if (!activeGmTargetAction) return;
    let panel = document.getElementById(GM_TARGET_ACTION_PANEL_ID);
    if (!panel) {
        panel = document.createElement('div');
        panel.id = GM_TARGET_ACTION_PANEL_ID;
        // Dark floating surface (css/src/97-ft3d-hud.css) — it sits over the 3D scene, where
        // Bootstrap's light `alert alert-info` read as a white slab punched through the map.
        // Position/geometry live in the stylesheet too; nothing but identity is set here.
        panel.className = 'ft-gta-panel';
        panel.setAttribute('role', 'status');
        document.body.appendChild(panel);
        panel.addEventListener('click', (event) => {
            const el = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-ft-gm-target-action]') : null;
            const action = el?.dataset.ftGmTargetAction;
            if (action === 'confirm') void confirmGmTargetAction();
            if (action === 'cancel') cancelGmTargetAction();
            if (action === 'fold') { gmTargetActionRosterOpen = !gmTargetActionRosterOpen; renderGmTargetAction(); }
            // Trim a mixed selection down to the picks that already carry the flag, so the next
            // Confirm clears them instead of converging everything the other way.
            if (action === 'keep-on') gmTargetActionKeepOn();
            // Clicking a chip removes that creature — same path as un-picking it in the scene, so the
            // 3D highlight and the roster can never disagree.
            if (action === 'drop' && el?.dataset.ftGmTargetEntity) {
                gmTargetActionPickEntity(el.dataset.ftGmTargetEntity);
            }
        });
    }
    const { label, minTargets, maxTargets } = activeGmTargetAction.options;
    const count = activeGmTargetAction.selected.length;
    const ready = count >= minTargets && count <= maxTargets && !activeGmTargetAction.busy;
    const ceiling = maxTargets >= 1000 ? '' : ` / ${maxTargets}`;
    const picks = gmTargetActionPicks();
    const direction = gmTargetActionDirection(picks);

    // Confirm SAYS the direction ("Hide 2" / "Reveal 2") rather than a neutral "Confirm", because the
    // endpoints converge the whole selection one way and the GM otherwise cannot tell which way.
    const confirmLabel = direction ? `${direction.verb} ${count}` : 'Confirm';
    // 'on' = applying the flag (amber, cautious), 'off' = clearing it (green), '' = unknown/neutral.
    const confirmDirection = direction ? (direction.turningOn ? 'on' : 'off') : '';

    const roster = gmTargetActionRosterOpen ? gmTargetActionRosterHtml(picks) : '';
    const chevron = gmTargetActionRosterOpen ? '&#9662;' : '&#9656;';

    // innerHTML replacement drops the roster's scroll position; dropping a chip from a scrolled
    // roster would otherwise snap the GM back to the top mid-curation.
    const keptScroll = panel.querySelector<HTMLElement>('[data-ft-gm-roster]')?.scrollTop ?? 0;

    panel.innerHTML = `
        ${roster}
        ${gmTargetActionRosterOpen ? gmTargetActionTrimHtml(picks) : ''}
        <div class="ft-gta-controls">
            <button type="button" class="ft-gta-btn ft-gta-fold"
                data-ft-gm-target-action="fold"
                title="${gmTargetActionRosterOpen ? 'Hide' : 'Show'} the selected creatures">${chevron}</button>
            <span class="ft-gta-prompt">${activeGmTargetAction.busy
                ? `Applying ${escapeHtml(label)}&hellip;`
                : `${escapeHtml(label)}: click creatures in the Scene or Combat Widget (${count}${ceiling}).`}</span>
            <button type="button" class="ft-gta-btn ft-gta-confirm" data-direction="${confirmDirection}"
                data-ft-gm-target-action="confirm" ${ready ? '' : 'disabled'}>${escapeHtml(confirmLabel)}</button>
            <button type="button" class="ft-gta-btn" data-ft-gm-target-action="cancel" ${activeGmTargetAction.busy ? 'disabled' : ''}>Cancel</button>
        </div>`;

    if (keptScroll > 0) {
        const rebuilt = panel.querySelector<HTMLElement>('[data-ft-gm-roster]');
        if (rebuilt) rebuilt.scrollTop = keptScroll;
    }
}

function cancelGmTargetAction(): void {
    if (!activeGmTargetAction) {
        window.ftReleaseInteractionMode?.('gm-target-action');
        return;
    }
    activeGmTargetAction = null;
    window.removeEventListener('message', onGmTargetActionMessage);
    window.removeEventListener('keydown', onGmTargetActionKeyDown, true);
    postGmTargetActionPickMode(false);
    postGmTargetActionSelection([]);
    document.getElementById(GM_TARGET_ACTION_PANEL_ID)?.remove();
    cancelTargetDraft('gm-target-action-cancel');
    window.ftReleaseInteractionMode?.('gm-target-action');
}

function onGmTargetActionKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || !activeGmTargetAction || activeGmTargetAction.busy) return;
    event.preventDefault();
    cancelGmTargetAction();
}

function onGmTargetActionMessage(event: MessageEvent): void {
    if (!activeGmTargetAction || event.origin !== window.location.origin
        || event.source !== gmTargetActionFrame()?.contentWindow) return;
    const envelope = event.data as { type?: unknown; payload?: { entityId?: unknown } } | null;
    if (envelope?.type !== 'ft3d-campaign-creature-pick-v1') return;
    gmTargetActionPickEntity(String(envelope.payload?.entityId ?? ''));
}

function gmTargetActionPickEntity(entityId: string): boolean {
    if (!activeGmTargetAction || activeGmTargetAction.busy) return false;
    const id = String(entityId ?? '').trim().toUpperCase();
    if (!/^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/.test(id)) return false;
    const selected = activeGmTargetAction.selected;
    const index = selected.indexOf(id);
    if (index >= 0) selected.splice(index, 1);
    else if (selected.length < activeGmTargetAction.options.maxTargets) selected.push(id);
    applyTargetEntityIds(selected, `gm-target-action:${activeGmTargetAction.options.key}`);
    postGmTargetActionSelection(selected);
    renderGmTargetAction();
    return true;
}

/** Drop every pick that does NOT already carry the axis flag, flipping the converge direction. */
function gmTargetActionKeepOn(): void {
    if (!activeGmTargetAction || activeGmTargetAction.busy) return;
    const keep = gmTargetActionPicks().filter((pick) => pick.resolved && pick.on).map((pick) => pick.id);
    if (keep.length === 0 || keep.length === activeGmTargetAction.selected.length) return;
    activeGmTargetAction.selected = keep;
    applyTargetEntityIds(keep, `gm-target-action:${activeGmTargetAction.options.key}`);
    postGmTargetActionSelection(keep);
    renderGmTargetAction();
}

/** A departure removes only its own picks; an unrelated armed action retains ownership. */
function retireDepartedTargetPicks(ids: readonly string[]): void {
    const removed = new Set(ids.map(id => id.toUpperCase()));
    if (!removed.size) return;
    if (activeGmTargetAction?.selected.some(id => removed.has(id.toUpperCase()))) {
        activeGmTargetAction.selected = activeGmTargetAction.selected.filter(id => !removed.has(id.toUpperCase()));
        postGmTargetActionSelection(activeGmTargetAction.selected);
        renderGmTargetAction();
    }
    const { entityIds } = parseTargetIdField(window.ftGetTargetSelection?.());
    if (entityIds.some(id => removed.has(id.toUpperCase())))
        applyTargetEntityIds(entityIds.filter(id => !removed.has(id.toUpperCase())), 'creature-departure');
}
window.addEventListener('ft:encounter-rows-patched', event => {
    const ids = (event as CustomEvent<{ removedIds?: unknown }>).detail?.removedIds;
    if (Array.isArray(ids)) retireDepartedTargetPicks(ids.filter((id): id is string => typeof id === 'string'));
});

async function confirmGmTargetAction(): Promise<void> {
    const active = activeGmTargetAction;
    if (!active || active.busy) return;
    const ids = [...active.selected];
    if (ids.length < active.options.minTargets || ids.length > active.options.maxTargets) return;
    active.busy = true;
    renderGmTargetAction();
    try {
        await active.options.onConfirm(ids);
    } finally {
        cancelGmTargetAction();
    }
}

/** Ability-first equivalent for GM tools which genuinely operate on one or more creatures. */
function startGmTargetAction(options: GmTargetActionOptions): void {
    const key = String(options?.key ?? '').trim();
    const label = String(options?.label ?? '').trim() || 'GM action';
    if (!key || typeof options?.onConfirm !== 'function') return;
    if (activeGmTargetAction) cancelGmTargetAction();
    window.ftClaimInteractionMode?.('gm-target-action', cancelGmTargetAction);
    cancelTargetDraft('gm-target-action-arm');
    activeGmTargetAction = {
        options: {
            key,
            label,
            minTargets: Math.max(1, Math.trunc(Number(options.minTargets ?? 1)) || 1),
            maxTargets: Math.max(1, Math.trunc(Number(options.maxTargets ?? 1000)) || 1000),
            stateAxis: (options.stateAxis === 'hidden' || options.stateAxis === 'private') ? options.stateAxis : null,
            onConfirm: options.onConfirm,
        },
        selected: [],
        busy: false,
    };
    window.addEventListener('message', onGmTargetActionMessage);
    window.addEventListener('keydown', onGmTargetActionKeyDown, true);
    postGmTargetActionPickMode(true);
    renderGmTargetAction();
}

// =============================================================================
// TARGETING / COMBAT WIDGET
// targetObject, explicit cleanup APIs, combat-widget thumbnail sync, AOE selection,
// turn-timer, target-count helpers. Heart of the combat-targeting UX.
// =============================================================================

/**
 * Click-handler for any clickable object in the encounter.
 *   • If the click is inside the combat widget → find the *nearest* map creature
 *     with the same name and target that one instead.
 *   • Otherwise → target the clicked element (old behaviour).
 */
function targetObject(clickedElement: HTMLElement | null | undefined) {
    if (!clickedElement) return;

    // ——————— Native FT3D combat strip click → Target by ID ———————
    const combatWidget = clickedElement.closest<HTMLElement>('.ft3d-combat-strip');
    if (combatWidget) {
        const thumb = clickedElement.closest<HTMLElement>('[data-encounter_creature_id]');
        if (thumb?.dataset.is_lair === '1') {
            return;
        }
        if (thumb?.dataset.encounter_creature_id) {
            routeCombatantClick(thumb.dataset.encounter_creature_id, 'ft3d-combat-strip');
            return;
        }
        return; // no match
    }

    // ——————— Normal click on map / inventory ———————
    let el: HTMLElement | null = clickedElement;
    while (el && el !== document.body) {
        if (el.classList?.contains('ft3d-combat-strip')) {
            el = el.parentElement;
            continue;
        }
        const id = el.dataset.encounter_creature_id ||
                   el.dataset.inventory_id ||
                   el.dataset.status_id;
        if (id) {
            el.classList.toggle('targeted');
            updateTargetedObjects();
            return;
        }
        el = el.parentElement;
    }
}

/** Route a combatant click through the interaction that explicitly armed creature picking.
 * Neutral initiative-strip clicks are intentionally inert: selecting an ability, Roll Initiative,
 * or the GM's jump-turn control must establish the meaning of the next creature click first. */
function routeCombatantClick(entityId: unknown, source = 'combatant-click'): boolean {
    const id = String(entityId ?? '').trim().toUpperCase();
    if (!id) return false;

    if (window.ftTurnJumpPickEntity?.(id)) return true;
    if (window.ftCombatPickEntity?.(id)) return true;
    if (window.ftGmTargetActionPickEntity?.(id)) return true;

    const active = window.__ftAbilityTargetingRuntime?.lifecycle?.current?.();
    if (!active || typeof window.ftAbilityTargetingStagePick !== 'function') return false;
    const pickId = `${active.intentId}:${id}:${source}:${Math.round(performance.now() * 1000)}`;
    // Host portraits must ask the scene to attempt the pick, just like a world click:
    // it owns legality feedback and the ordered Reach approach before staging a target.
    if (source !== 'ft3d-campaign') {
        const request = new CustomEvent('ft:ability-targeting-portrait-pick', {
            cancelable: true,
            detail: { intentId: active.intentId, entityId: id, pickId },
        });
        if (!window.dispatchEvent(request)) return true;
    }
    return window.ftAbilityTargetingStagePick(active.intentId, id, pickId);
}

/**
 * Parse the target_id selection string like updateTargetedObjects(): optional first segment "x|z", then comma-separated entity UUIDs.
 * @returns { mapPointToken: string|null, entityIds: string[] }
 */
function parseTargetIdField(raw: unknown) {
    const value = String(raw ?? '').trim();
    if (!value) return { mapPointToken: null, entityIds: [] };
    const parts = value.split(',');
    const first = (parts[0] ?? '').trim();
    if (first.includes('|')) {
        const rest = parts.slice(1).map((s: string) => s.trim()).filter(Boolean);
        return { mapPointToken: first, entityIds: rest };
    }
    return { mapPointToken: null, entityIds: parts.map((s: string) => s.trim()).filter(Boolean) };
}

/** Mirrors PHP is_target_encounter_format() for client-side checks. */
function isTargetEncounterFormatString(str: unknown) {
    const s = String(str ?? '').trim();
    if (!s.includes(':')) return false;
    return /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}\|[^|,]+$/i.test(s);
}

/**
 * Approximate count of targets PHP builds from the target_id field (see standard_inputs_v2_inc.php).
 * Use for UI gates so client matches server comma-split semantics.
 */
function getStandardInputTargetCount(raw: unknown) {
    let s = String(raw ?? '').trim().replace(/,+$/g, '');
    if (!s) return 0;
    if (s.includes(',')) {
        return s.split(',').map((p: string) => p.trim()).filter(Boolean).length;
    }
    if (isTargetEncounterFormatString(s)) return 1;
    if (checkIfValidUUID(s)) return 1;
    return 0;
}

const FT_AOE_LAST_ANCHOR_KEY = 'ft_aoe_last_anchor_v1';

type AoeLastAnchorBlob = Record<string, string>;

function readAoeLastAnchorBlob(): AoeLastAnchorBlob {
    try {
        const raw = sessionStorage.getItem(FT_AOE_LAST_ANCHOR_KEY);
        if (!raw) return {};
        const o: unknown = JSON.parse(raw);
        if (!o || typeof o !== 'object' || Array.isArray(o)) return {};
        const blob: AoeLastAnchorBlob = {};
        for (const [encounterId, token] of Object.entries(o)) {
            if (typeof token === 'string') blob[encounterId] = token;
        }
        return blob;
    } catch {
        return {};
    }
}

/** Last map/AOE geometry token per encounter — saved on broad legacy cleanup for optional AOE re-place (no cookies). */
function writeAoeLastAnchorForEncounter(encounterId: unknown, mapPointToken: unknown) {
    if (!encounterId || !mapPointToken) return;
    const blob = readAoeLastAnchorBlob();
    blob[String(encounterId)] = String(mapPointToken);
    try {
        sessionStorage.setItem(FT_AOE_LAST_ANCHOR_KEY, JSON.stringify(blob));
    } catch {
        /* quota / private mode */
    }
}

function getCachedAoeLastAnchor(encounterId: unknown) {
    if (!encounterId) return '';
    const blob = readAoeLastAnchorBlob();
    return blob[String(encounterId)] ?? '';
}

if (typeof window.FateTide === 'object' && window.FateTide) {
    window.FateTide.getStandardInputTargetCount = getStandardInputTargetCount;
    window.FateTide.getCachedAoeLastAnchor = getCachedAoeLastAnchor;
    window.FateTide.abilityDropTargetFailure = abilityDropTargetFailure;
}

/**
 * Mirror canonical selection onto the FT3D combat-strip thumbnails.
 */
function syncFt3dCombatStripTargets() {
    const selected = new Set(parseTargetIdField(window.ftGetTargetSelection?.()).entityIds.map((id) => id.toUpperCase()));
    document.querySelectorAll<HTMLElement>('.ft3d-combat-strip [data-encounter_creature_id]').forEach((thumb) => {
        const id = String(thumb.dataset.encounter_creature_id || '').toUpperCase();
        thumb.classList.toggle('targeted', selected.has(id));
    });
}

function dispatchTargetStateChange(entityIds?: readonly string[]) {
    const ids = entityIds ?? [
        ...new Set(
            Array.from(document.querySelectorAll<HTMLElement>('.targeted'))
                .filter((el) => !el.closest('.ft3d-combat-strip'))
                .map((el) => el.dataset.encounter_creature_id
                    || el.dataset.inventory_id
                    || el.dataset.status_id)
                .filter((id): id is string => Boolean(id)),
        ),
    ];
    window.dispatchEvent(new CustomEvent('ft:targets-changed', {
        detail: { entityIds: [...ids] },
    }));
}

/** Apply an explicit entity-id target set without requiring a rendered map token.
 * FT3D player routes use their perceived
 * 3D placements must be able to drive the same canonical selection helper by identity. */
function applyTargetEntityIds(entityIds: readonly string[], source: string): void {
    const ids: string[] = [];
    const normalized = new Set<string>();
    entityIds.forEach((value) => {
        const id = String(value ?? '').trim();
        const key = id.toUpperCase();
        if (!id || normalized.has(key)) return;
        normalized.add(key);
        ids.push(id);
    });

    document.querySelectorAll<HTMLElement>(
        '[data-encounter_creature_id], [data-inventory_id], [data-status_id]',
    ).forEach((element) => {
        const id = element.dataset.encounter_creature_id
            || element.dataset.inventory_id
            || element.dataset.status_id
            || '';
        element.classList.toggle('targeted', normalized.has(id.toUpperCase()));
    });

    const { mapPointToken } = parseTargetIdField(window.ftGetTargetSelection?.());
    const nextSelection = mapPointToken
        ? (ids.length ? `${mapPointToken},${ids.join(',')}` : mapPointToken)
        : ids.join(',');
    window.ftSetTargetSelection?.(nextSelection, source);
    syncFt3dCombatStripTargets();
    syncEncounterLightSourceAbilityButtons();
    syncAbilityButtonTargetCompatibility();
    dispatchTargetStateChange(ids);
}

/** Toggle one entity in the canonical selection by id. This is the mapless counterpart to
 * targetObject(): callers have already resolved a visible/permitted encounter presentation. */
function toggleTargetEntityId(entityId: unknown, source = 'targeting-combat-id'): boolean {
    const id = String(entityId ?? '').trim();
    if (!id) return false;
    const { entityIds } = parseTargetIdField(window.ftGetTargetSelection?.());
    const wanted = id.toUpperCase();
    const alreadyTargeted = entityIds.some((candidate) => candidate.toUpperCase() === wanted);
    const nextIds = alreadyTargeted
        ? entityIds.filter((candidate) => candidate.toUpperCase() !== wanted)
        : [...entityIds, id];
    applyTargetEntityIds(nextIds, source);
    return !alreadyTargeted;
}

// ─────────────────────────────────────────────────────────────────────────────
// SINGLE-TARGET ABILITY-BUTTON COMPATIBILITY GATE
// Client mirror of the server's ability_target_gate_failure(): when EXACTLY ONE creature is targeted,
// amber-ring any ability button whose target gate (size / creature type / target status) the target fails.
// The same predicate is exported for the click-time preflight, which warns and aborts before the ability
// endpoint is called. Gate spec is stamped on the button as data-* attrs by generateAbilityButton().
// FAIL-OPEN: any unreadable trait → no client block; the server stays authoritative. Status-ACTIVE
// gates are intentionally NOT evaluated here (absence can't be proven from the blob alone — v_status /
// grapple-pair), only status-NOT-active (warns on detected presence).
// ─────────────────────────────────────────────────────────────────────────────

function ftSizeRankMap(): Record<string, number> {
    const out: Record<string, number> = {};
    const list = window.SIZE_TYPES;
    if (Array.isArray(list)) {
        list.forEach((row, i) => {
            const id = String(row?.item_id ?? '').toUpperCase();
            if (id) out[id] = i;
        });
    }
    return out;
}

/** Resolve a creature row blob (item-arr shape) from the retained encounter cache. */
function ftFindTargetedCreatureBlob(ecId: string): unknown | null {
    if (!ecId) return null;
    const want = ecId.toUpperCase();
    return targetingCreatures().find((row) => {
        const record = targetingRecord(row);
        const id = String(ftEncounterCreatureField(record?.encounter_creatures ?? row, 'encounter_creature_id') ?? '').toUpperCase();
        return id === want;
    }) ?? null;
}

function ftCreatureTraitUuids(blob: unknown, key: string): Set<string> {
    const out = new Set<string>();
    let entries: unknown[] = [];
    try {
        entries = ftLayerEntries(blob, key);
    } catch {
        const value = ftLayerValue(blob, key);
        entries = value == null ? [] : (Array.isArray(value) ? value : [value]);
    }
    for (const entry of entries) {
        const id = entry && typeof entry === 'object' ? ftItemsCol(entry, 'item_id') : entry;
        const normalized = String(id ?? '').toUpperCase();
        if (normalized) out.add(normalized);
    }
    return out;
}

/**
 * Final size UUID for a creature blob — computed bridge FIRST (mirrors PHP layer_value('size')
 * → computed.creature.size.breakdown.final_size_id). A raw trait-uuid read can surface a
 * taxonomy carrier's BASE size now that PC blobs retain the creature_sub_type carrier slot,
 * which would diverge from the server gate on resized creatures.
 */
function ftCreatureFinalSizeId(blob: unknown): string {
    const rec = targetingRecord(blob);
    const bridged = (rec?.computed as Record<string, any> | undefined)?.creature?.size?.breakdown?.final_size_id;
    if (typeof bridged === 'string' && bridged !== '') return bridged.toUpperCase();
    return [...ftCreatureTraitUuids(blob, 'size')][0] ?? '';
}

function ftCreatureActiveStatusIds(blob: unknown): Set<string> {
    const out = new Set<string>();
    const statuses = ftCreatureStatuses(blob, 'target');
    if (Array.isArray(statuses)) {
        statuses.forEach((st) => {
            const id = String(ftItemsCol(st, 'item_id') ?? '').toUpperCase();
            if (id) out.add(id);
        });
    }
    return out;
}

interface FtAbilityGateTargetTraits {
    casterSize?: string;
    tgtSize: string;
    tgtTypes: Set<string>;
    tgtSubs: Set<string>;
    tgtStatuses: Set<string>;
    sizeRank: Record<string, number>;
}

function ftAbilityGateFailureForTarget(
    btn: HTMLElement,
    t: FtAbilityGateTargetTraits,
): string | null {
    const d = btn.dataset;
    const csv = (s?: string) => (s ? s.split(',').map((x) => x.trim().toUpperCase()).filter(Boolean) : []);
    const abilityName = String(d.bsOriginalTitle ?? 'This ability').split(' — ', 1)[0] || 'This ability';
    const intersects = (left: Set<string>, right: string[]) => right.some((id) => left.has(id));

    // Inventory-only (every caller here judges a CREATURE target) — mirrors the server's first gate.
    if (d.gate_inventory_only === '1') {
        return `${abilityName} can only target inventory — objects and structures, not creatures.`;
    }

    // Size — fail-safe: only judge when both caster + target rank are known.
    const cRank = t.sizeRank[String(t.casterSize || d.gate_caster_size || '').toUpperCase()];
    const tRank = t.sizeRank[t.tgtSize];
    if (cRank !== undefined && tRank !== undefined) {
        if ((d.gate_sz_above ?? '') !== '') {
            const n = parseInt(d.gate_sz_above as string, 10);
            if (!Number.isNaN(n) && !(tRank >= cRank + n)) {
                return `${abilityName} cannot affect a target this much smaller than the caster.`;
            }
        } else if ((d.gate_sz_below ?? '') !== '') {
            const n = parseInt(d.gate_sz_below as string, 10);
            if (!Number.isNaN(n) && !(tRank <= cRank + n)) {
                return `${abilityName} cannot affect a target this much larger than the caster.`;
            }
        }
    }

    // Creature type / sub-type whitelist + blacklist. Compare complete layer sets, matching PHP's
    // bucket-aware layer_entries() behavior for additive carrier classifications.
    if (t.tgtTypes.size) {
        const only = csv(d.gate_type_only);
        if (only.length && !intersects(t.tgtTypes, only)) {
            return `${abilityName} only works against certain creature types — this target does not qualify.`;
        }
        const not = csv(d.gate_type_not);
        if (not.length && intersects(t.tgtTypes, not)) {
            return `${abilityName} does not work against this creature's type.`;
        }
    }
    if (t.tgtSubs.size) {
        // 2026-07-15 division restrictions — every division (lineage/ancestry/grouping) attaches
        // via the same creature_sub_type layer, so they all match against the SAME target set.
        // The WHITELISTS evaluate as ONE UNION across sub_type + the three divisions (an authored
        // OR spanning division layers must not become an AND — mirrors the server gate exactly);
        // blacklists stay per-attr (equivalent either way).
        const onlySources: Array<[string | undefined, string]> = [
            [d.gate_subtype_only,  'sub-types'],
            [d.gate_lineage_only,  'lineages'],
            [d.gate_ancestry_only, 'ancestries'],
            [d.gate_grouping_only, 'groupings'],
        ];
        const unionOnly: string[] = [];
        const unionLabels: string[] = [];
        for (const [attr, plural] of onlySources) {
            const ids = csv(attr);
            if (ids.length) { unionOnly.push(...ids); unionLabels.push(plural); }
        }
        if (unionOnly.length && !intersects(t.tgtSubs, unionOnly)) {
            const plural = unionLabels.length === 1 ? unionLabels[0] : 'classifications';
            return `${abilityName} only works against certain creature ${plural} — this target does not qualify.`;
        }
        const notSources: Array<[string | undefined, string]> = [
            [d.gate_subtype_not,  'sub-type'],
            [d.gate_lineage_not,  'lineage'],
            [d.gate_ancestry_not, 'ancestry'],
            [d.gate_grouping_not, 'grouping'],
        ];
        for (const [attr, singular] of notSources) {
            const ids = csv(attr);
            if (ids.length && intersects(t.tgtSubs, ids)) {
                return `${abilityName} does not work against this creature's ${singular}.`;
            }
        }
    }

    // Target must NOT have a status — warn only on DETECTED presence (fail-safe).
    const stNot = csv(d.gate_status_not);
    if (stNot.length && stNot.some((s) => t.tgtStatuses.has(s))) {
        return `The target has a status effect active that prevents ${abilityName} from affecting it.`;
    }

    return null;
}

function ftAbilityGateButton(abilityId: unknown): HTMLElement | null {
    const wanted = String(abilityId ?? '').toUpperCase();
    if (!wanted) return null;
    return Array.from(document.querySelectorAll<HTMLElement>('.btn_ability_ranges[data-ft-target-gate="1"]'))
        .find((btn) => String(btn.dataset.ability_id ?? '').toUpperCase() === wanted) ?? null;
}

/**
 * Click-time client preflight. Returns a warning only when one cached creature target can be proven
 * incompatible; all unresolved, inventory, map-point, and multi-target cases fail open to Janus.
 */
function abilityTargetGateWarning(abilityId: unknown, casterId: unknown, targetIds: unknown[]): string | null {
    const ids = [...new Set((Array.isArray(targetIds) ? targetIds : []).map((id) => String(id)).filter(Boolean))];
    if (ids.length !== 1) return null;
    const targetId = ids[0];
    if (!targetId) return null;
    const button = ftAbilityGateButton(abilityId);
    if (!button) return null;
    const blob = ftFindTargetedCreatureBlob(targetId);
    if (!blob) return null;
    const casterBlob = ftFindTargetedCreatureBlob(String(casterId ?? ''));
    return ftAbilityGateFailureForTarget(button, {
        sizeRank: ftSizeRankMap(),
        casterSize: casterBlob ? ftCreatureFinalSizeId(casterBlob) : '',
        tgtSize: ftCreatureFinalSizeId(blob),
        tgtTypes: ftCreatureTraitUuids(blob, 'creature_type'),
        tgtSubs: ftCreatureTraitUuids(blob, 'creature_sub_type'),
        tgtStatuses: ftCreatureActiveStatusIds(blob),
    });
}

/**
 * Per-creature drop-eligibility for the action-bar creature-target bridge (Feature B). Reuses the SAME
 * client gate as the target-compat ring — team (shared predicate) + size / creature-type / sub-type /
 * status-not — but for ONE creature id, without the single-target-only guard. Returns a human reason when
 * the creature is not a legal target, else null. ADVISORY & fail-open (null when traits can't be resolved):
 * the Janus cast-validation chain is authoritative (team skip, size/type/status nullify, item-type veto).
 */
function abilityDropTargetFailure(abilityId: unknown, casterId: unknown, creatureId: unknown): string | null {
    const cId = String(creatureId ?? '');
    if (!cId) return null;
    // Team first — an effect_only_affects_*_team_type ability need not carry a size/type gate button.
    if (window.abilityTeamGateWarning?.(String(abilityId ?? ''), String(casterId ?? ''), [cId])) {
        return 'That creature is on the wrong team for this ability.';
    }
    const button = ftAbilityGateButton(abilityId);
    if (!button) return null; // no size/type/status gate on this ability → team decided it above
    const blob = ftFindTargetedCreatureBlob(cId);
    if (!blob) return null; // unresolved traits → fail open to the server
    const casterBlob = ftFindTargetedCreatureBlob(String(casterId ?? ''));
    return ftAbilityGateFailureForTarget(button, {
        sizeRank: ftSizeRankMap(),
        casterSize: casterBlob ? ftCreatureFinalSizeId(casterBlob) : '',
        tgtSize: ftCreatureFinalSizeId(blob),
        tgtTypes: ftCreatureTraitUuids(blob, 'creature_type'),
        tgtSubs: ftCreatureTraitUuids(blob, 'creature_sub_type'),
        tgtStatuses: ftCreatureActiveStatusIds(blob),
    });
}

function syncAbilityButtonTargetCompatibility() {
    // Also pick up team-flag-only buttons: an effect_only_affects_*_team_type ability need not carry a
    // size/type/status gate (data-ft-target-gate), so it would otherwise be missed — select it explicitly
    // and let the shared team predicate below flag it.
    const buttons = document.querySelectorAll<HTMLElement>(
        '.btn_ability_ranges[data-ft-target-gate="1"], '
        + '.btn_ability_ranges[data-effect_only_affects_opposing_team_type="1"], '
        + '.btn_ability_ranges[data-effect_only_affects_same_team_type="1"]',
    );
    if (!buttons.length) return;

    const clear = () => buttons.forEach((button) => button.classList.remove('ft-ability-btn-target-incompatible'));

    // Exactly one creature targeted? Ignore inventory and map-point targets.
    const creatureIds = [
        ...new Set(
            Array.from(document.querySelectorAll<HTMLElement>('.targeted'))
                .map((el) => el.dataset.encounter_creature_id)
                .filter(Boolean),
        ),
    ];
    if (creatureIds.length !== 1) { clear(); return; }
    const targetId = String(creatureIds[0]);

    const blob = ftFindTargetedCreatureBlob(targetId);
    if (!blob) { clear(); return; }

    const t = {
        sizeRank: ftSizeRankMap(),
        tgtSize: ftCreatureFinalSizeId(blob),
        tgtTypes: ftCreatureTraitUuids(blob, 'creature_type'),
        tgtSubs: ftCreatureTraitUuids(blob, 'creature_sub_type'),
        tgtStatuses: ftCreatureActiveStatusIds(blob),
    };

    // Team advisory: reuse the SAME shared predicate as the click-time block (team-filters.ts) so the ring
    // and the block can't drift. Fail-open — null when it can't prove wrong-team (blob/team unresolved, aura,
    // persistent_aoe, neutral). Caster from the active controller; target is the single creature above.
    const casterId = String(window.getActiveControllerCasterId?.() || '');
    const teamFailsFor = (btn: HTMLElement): boolean =>
        !!window.abilityTeamGateWarning?.(btn.dataset.ability_id, casterId, [targetId]);

    buttons.forEach((btn) => {
        btn.classList.toggle(
            'ft-ability-btn-target-incompatible',
            ftAbilityGateFailureForTarget(btn, t) !== null || teamFailsFor(btn),
        );
    });
}

function updateTargetedObjects() {
    const targeted = document.querySelectorAll<HTMLElement>('.targeted');

    const ids = [
        ...new Set(
            Array.from(targeted)
                .map(
                    (el) =>
                        el.dataset.encounter_creature_id ||
                        el.dataset.inventory_id ||
                        el.dataset.status_id,
                )
                .filter((id): id is string => Boolean(id)),
        ),
    ];

    applyTargetEntityIds(ids, 'targeting-combat');
}

// CLEAR CASTER – remove caster visuals only (no widget reset)
function clearCaster() {
    // 1. Clear the active caster (cache is the source of truth — hidden inputs deleted 2026-06-22)
    // + GM viewer. Remember the previous active caster for the WebSocket cleanup below. masterCasterId
    // is left intact.
    const gmViewer = document.getElementById('div_refresh_gm_viewer');
    const prevCasterId = String(window.ftGetActiveCasterId?.() ?? '').trim() || null;

    const ft = window.FateTide;
    if (ft) ft.activeCasterId = null;
    if (gmViewer) gmViewer.innerHTML = '';

    // 2. Clear selection (if exists)
    if (typeof clearSelection === 'function') {
        clearSelection();
    }

    // 3. Broad caster/context teardown (legacy panels + any active placement).
    clearLegacySelection();

    // 4. WebSocket: Unsubscribe from old caster channel
    if (prevCasterId && checkIfValidUUID(prevCasterId)) {
        safeUnsubscribe(`caster:${prevCasterId}`);
        ftDebugLog('caster', { unsubscribed: `caster:${prevCasterId}` });
    }

    // 5. Reset caster-channel bookkeeping (moved off the #caster_id element to window.FateTide
    // with the hidden-input deprecation 2026-06-22).
    const ftClear = window.FateTide;
    if (ftClear) { ftClear.prevCasterId = null; ftClear.casterChannelSub = null; }

    // ———————————————————————————————————————————————
    // 6. CRITICAL: Clear the active viewer caster globally
    // ———————————————————————————————————————————————
    if (window.FateTide!.activeViewerCasterId) {
        const clearedId = window.FateTide!.activeViewerCasterId;
        window.FateTide!.activeViewerCasterId = null;
        ftDebugLog('caster', { activeViewerCleared: clearedId });
    }

	ftDebugLog('caster', { cleared: true });
}

/** Clear only the transient target draft owned by one completed/cancelled action.
 * Deliberately leaves side panels, placement controllers, and post-render work alone. */
function cancelTargetDraft(reason = 'cancel-target-draft') {
    window.ftSetTargetSelection?.('', reason);
    document.querySelectorAll('.targeted').forEach(el => el.classList.remove('targeted'));
    syncFt3dCombatStripTargets();
    syncEncounterLightSourceAbilityButtons();
    syncAbilityButtonTargetCompatibility();
    dispatchTargetStateChange([]);
}

/** Broad XZ/scene teardown retained for caster/encounter transitions and explicit legacy cleanup. */
function clearLegacySelection() {
    const raw = String(window.ftGetTargetSelection?.() || '');
    const { mapPointToken } = parseTargetIdField(raw);
    const eid = (window.ftGetActiveEncounterId?.() || '').trim();
    if (mapPointToken && eid) {
        writeAoeLastAnchorForEncounter(eid, mapPointToken);
    }
    cancelTargetDraft('clear-legacy-selection');

    // Remove selection point
    document.querySelectorAll('.selection-point').forEach(el => el.remove());

    // Clear side panel (tear down TomSelect/tooltips/charts first)
    clearSideInfoPanelContent();

    // ————————————————————————
    // CANCEL ACTIVE AOE EDITING (persistent or temporary)
    // ————————————————————————
    if (window.aoeManager?.ft3dActive) {
        ftDebugLog('aoe', { clearingTargets: true, cancellingActiveAoe: true });
        window.aoeManager.cancelFt3d?.();
    }

    loadPostFunctions();
}

// =============================================================================
// Cross-file consumer exports
// Top-level `function foo() {}` declarations in the original non-module script
// auto-attached to `window`; the explicit assignments below preserve that surface
// now that this is an ES module. The `window.FateTide.*` exports for
// getStandardInputTargetCount + getCachedAoeLastAnchor live inside the body above.
// =============================================================================

// =============================================================================
// TARGET-STATE CHECKS (Phase 2g, 2026-06-01) — moved here from template.js's DOM UTILITIES junk-drawer.
// Predicates over a target's status_effect list: private / hidden / invisible / ethereal / spell-effect,
// plus the campaign-pause guard they all share. Consumed by template.js + the encounter-map bundle
// (XZVisibility) at runtime. Deps: ftDebugLog (template.js), ftItemsCol (item-arr-readers.js, early).
// =============================================================================

// Returns true only if at least one of the private flags is explicitly set to 1
function checkTargetPrivate(target: unknown) {
    const record = targetingRecord(target);
    if (!record) return false;

    // Encounter creature — most common. Current rows expose `encounter_creatures` (bare scalars); the
    // legacy `encounter_creature_arr` ([0]-wrapped) is kept as a fallback. Reading ONLY the legacy shape
    // meant private creatures were never flagged private (privacy/targeting gate silently open).
    const legacyCreature = targetingRecord(record.encounter_creature_arr);
    const legacyPrivate = legacyCreature?.encounter_creature_private;
    const ecPrivate = ftEncounterCreatureField(record.encounter_creatures, 'encounter_creature_private')
        ?? (Array.isArray(legacyPrivate) ? legacyPrivate[0] : legacyPrivate);
    if (ecPrivate == 1) {
        const legacyName = legacyCreature?.encounter_creature_name;
        const ecName = ftEncounterCreatureField(record.encounter_creatures, 'encounter_creature_name')
            ?? (Array.isArray(legacyName) ? legacyName[0] : legacyName);
		ftDebugLog('target private', { creature: ecName + ' is private' });
        return true;
    }

    // Inventory item — mirror the creature branch above. Current rows expose `inventory_private` as a
    // bare scalar (update_privacy_inc.php writes it flat via check_zero_or_one → 0/1); the legacy
    // `?.[0]` read returned undefined post-descalar, so private inventory was NEVER flagged private —
    // no XZ map fade, and the targeting/privacy gate silently open. Read via ftInventoryField
    // (bucket-or-flat, [0]-tolerant), keeping the legacy [0] shape as a fallback.
    const invPrivate = (typeof ftInventoryField === 'function'
            ? ftInventoryField(record, 'inventory_private')
            : undefined) ?? (Array.isArray(record.inventory_private) ? record.inventory_private[0] : record.inventory_private);
    if (invPrivate == 1) {
        const invName = (typeof ftInventoryField === 'function'
            ? ftInventoryField(record, 'inventory_name')
            : undefined) ?? (Array.isArray(record.inventory_name) ? record.inventory_name[0] : record.inventory_name);
		ftDebugLog('target private', { inventory: 'Inventory item ' + invName + ' is private' });
        return true;
    }


    // Fallback: direct status_private on the object
    const statusPrivate = record.status_private;
    if ((Array.isArray(statusPrivate) ? statusPrivate[0] : statusPrivate) == 1) {
		ftDebugLog('target private', { status: 'Status ' + String(record.status_name ?? '') + ' is private' });
        return true;
    }

    return false;
}

/**
 * Mirrors PHP status_instance_has_campaign_pause: any non-empty, non-zero
 * status_paused_campaign_* field means the instance is paused in campaign time.
 * @param inst Status row with [0]-wrapped fields
 */
function statusEffectInstanceIsCampaignPaused(inst: unknown) {
    const record = targetingRecord(inst);
    if (!record) return false;
    const keys = [
        'status_paused_campaign_year',
        'status_paused_campaign_month',
        'status_paused_campaign_day',
        'status_paused_campaign_hour',
        'status_paused_campaign_minute',
        'status_paused_campaign_second',
    ];
    for (const k of keys) {
        const raw = record[k];
        const v = Array.isArray(raw) ? raw[0] : raw;
        if (v === null || v === undefined || v === '') continue;
        if (typeof v === 'string' && v.trim() === '') continue;
        const num = Number(v);
        if (!Number.isNaN(num) && num === 0) continue;
        if (v === '0') continue;
        return true;
    }
    return false;
}

/**
 * Check if target is HIDDEN (status effect: 47667406-1B72-9D60-310B-D8CF6BFE397D)
 */
function checkTargetHidden(target: unknown) {
    return targetStatuses(target).some(
        (eff) =>
            !statusEffectInstanceIsCampaignPaused(eff) &&
            ftItemsCol(eff, 'item_id') === '47667406-1B72-9D60-310B-D8CF6BFE397D',
    );
}

/**
 * Check if target is INVISIBLE (Invisible or Greater Invisibility)
 */
function checkTargetInvisible(target: unknown) {
    return targetStatuses(target).some(
        (eff) =>
            !statusEffectInstanceIsCampaignPaused(eff) &&
            (ftItemsCol(eff, 'item_id') === '84B91EEB-2FA6-D1CB-1347-75BFBDA2A6DD' || // Invisible
                ftItemsCol(eff, 'item_id') === 'B6232175-C357-9A97-0ACA-7ECC67563635'), // Greater Invisibility
    );
}

/**
 * Check if target is ETHEREAL (status effect: 2A2845CD-949F-06E2-E6B2-98CFD5603241)
 */
function checkTargetEthereal(target: unknown) {
    return targetStatuses(target).some(
        (eff) =>
            !statusEffectInstanceIsCampaignPaused(eff) &&
            ftItemsCol(eff, 'item_id') === '2A2845CD-949F-06E2-E6B2-98CFD5603241',
    );
}

/**
 * Returns true if target has a spell-based status effect
 * Used for Spellsight sense
 */
function checkTargetSpellEffect(target: unknown) {
    return targetStatuses(target).some((effect) => {
        if (statusEffectInstanceIsCampaignPaused(effect)) return false;
        // Direct item_type check
        if (ftItemsCol(effect, 'item_type') === 'spell') return true;

        // Fallback: check inside status_arr
        const statusArr = targetingRecord(effect)?.status_arr;
        if (statusArr && ftItemsCol(statusArr, 'item_type') === 'spell') {
            return true;
        }

        return false;
    });
}

Object.assign(w, {
    clearCaster,
    cancelTargetDraft,
    clearLegacySelection,
    targetObject,
    toggleTargetEntityId,
    routeCombatantClick,
    startGmTargetAction,
    confirmGmTargetAction,
    cancelGmTargetAction,
    ftGmTargetActionPickEntity: gmTargetActionPickEntity,
    updateTargetedObjects,
    parseTargetIdField,
    isTargetEncounterFormatString,
    syncFt3dCombatStripTargets,
    getStandardInputTargetCount,
    getCachedAoeLastAnchor,
    abilityTargetGateWarning,
    checkTargetPrivate,
    statusEffectInstanceIsCampaignPaused,
    checkTargetHidden,
    checkTargetInvisible,
    checkTargetEthereal,
    checkTargetSpellEffect,
});

export {};
